import { motion } from 'framer-motion'
import { Shield, BarChart3, Database, DollarSign } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const features = [
  {
    title: "Real-Time Pattern Detection",
    description: "Instantly identify sequential phone numbers, datacenter IPs, and velocity attacks as they happen",
    icon: Shield,
    metrics: ["7 pattern types", "0.01s detection", "99% accuracy"],
    color: "text-red-400"
  },
  {
    title: "Smart Risk Scoring",
    description: "AI-powered risk assessment from 0-100 with configurable thresholds and ML confidence scores",
    icon: BarChart3,
    metrics: ["300+ data points", "ML confidence scores", "Custom weights"],
    color: "text-blue-400"
  },
  {
    title: "Bulk Lead Processing",
    description: "Process thousands of leads simultaneously with CSV import/export and batch operations",
    icon: Database,
    metrics: ["10k+ leads/minute", "5 export formats", "Bulk actions"],
    color: "text-green-400"
  },
  {
    title: "Financial Impact Tracking",
    description: "See exactly how much fraud prevention saves your business with detailed ROI analytics",
    icon: DollarSign,
    metrics: ["ROI dashboard", "Daily savings", "Cost per lead"],
    color: "text-purple-400"
  }
]

export const ProductFeatures = () => {
  return (
    <div className="py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Advanced Fraud Detection Features
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Comprehensive protection with real-time detection, intelligent scoring, and measurable business impact
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-8"
      >
        {features.map((feature, index) => {
          const Icon = feature.icon
          
          return (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ scale: 1.02 }}
              className="group"
            >
              <Card className="h-full bg-card/50 backdrop-blur-sm border-border hover:border-primary/20 transition-all duration-300">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-primary/10 group-hover:bg-primary/20 transition-colors`}>
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {feature.metrics.map((metric, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-secondary/50 text-secondary-foreground text-sm rounded-full border border-secondary"
                      >
                        {metric}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  )
}